/**
 * @file api-admin.ts
 * @description Admin API functions for user and plan management
 * @author XiaVan Team
 * 
 * Provides CRUD operations for:
 * - User management (list, add, remove, update role)
 * - Plan image management (get, update)
 * - Requires admin role to execute
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
  query,
  orderBy,
  where,
} from 'firebase/firestore'
import { db } from './firebase'
import type { User, AllowedEmail, AddUserDTO, UpdateUserRoleDTO } from '@/types/user'
import type { Plan } from '@/types/plan'

/**
 * Get all users from Firestore
 * Combines users collection with allowedEmails to show all authorized users
 * (including those who haven't logged in yet)
 * @returns Array of users with their roles and profile data
 * @throws Error if not authorized or query fails
 */
export async function getUsers(): Promise<User[]> {
  try {
    console.log('🔍 [getUsers] Fetching users from Firestore...')
    
    // 1. Get all users who have logged in
    const usersRef = collection(db, 'users')
    const usersQuery = query(usersRef, orderBy('createdAt', 'desc'))
    const usersSnapshot = await getDocs(usersQuery)
    
    const loggedInUsers = usersSnapshot.docs.map((docSnap) => {
      const data = docSnap.data()
      return {
        id: docSnap.id,
        ...data,
      } as User
    })
    
    console.log('📊 [getUsers] Found', loggedInUsers.length, 'logged-in users')
    
    // 2. Get all allowed emails (no orderBy to avoid issues with missing fields)
    const allowedEmailsRef = collection(db, 'allowedEmails')
    const allowedSnapshot = await getDocs(allowedEmailsRef)
    
    console.log('📊 [getUsers] Found', allowedSnapshot.size, 'allowed emails')
    
    // 3. Create map of logged-in users by email for fast lookup
    const loggedInEmailsMap = new Map<string, User>()
    loggedInUsers.forEach(user => {
      loggedInEmailsMap.set(user.email.toLowerCase(), user)
    })
    
    // 4. Merge: Start with logged-in users, add pending users from allowedEmails
    const allUsers: User[] = [...loggedInUsers]
    
    let pendingUsersCount = 0
    allowedSnapshot.docs.forEach((allowedDoc) => {
      const allowedData = allowedDoc.data()
      const email = allowedData.email.toLowerCase()
      
      console.log('🔍 [getUsers] Processing allowedEmail:', email, 'already logged in?', loggedInEmailsMap.has(email))
      
      // If user hasn't logged in yet, create placeholder entry
      if (!loggedInEmailsMap.has(email)) {
        console.log('👤 [getUsers] Adding pending user:', email)
        pendingUsersCount++
        allUsers.push({
          id: `pending_${email.replace(/[@.]/g, '_')}`, // Temporary ID
          email: allowedData.email,
          displayName: allowedData.email.split('@')[0], // Placeholder name
          photoURL: undefined,
          role: allowedData.role,
          requestedAt: allowedData.requestedAt || null,
          approvedAt: allowedData.approvedAt || allowedData.addedAt,
          approvedBy: allowedData.addedBy,
          createdAt: allowedData.addedAt,
          lastLogin: null as any, // Never logged in yet
        })
      }
    })
    
    console.log('✅ [getUsers] Total:', allUsers.length, 'users (', loggedInUsers.length, 'logged-in +', pendingUsersCount, 'pending )')
    return allUsers
  } catch (error) {
    console.error('❌ [getUsers] Error fetching users:', error)
    throw error
  }
}

/**
 * Get all allowed emails from Firestore
 * @returns Array of allowed emails with role information
 * @throws Error if not authorized or query fails
 */
export async function getAllowedEmails(): Promise<AllowedEmail[]> {
  try {
    const allowedEmailsRef = collection(db, 'allowedEmails')
    const q = query(allowedEmailsRef, orderBy('addedAt', 'desc'))
    const snapshot = await getDocs(q)

    return snapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
    })) as AllowedEmail[]
  } catch (error) {
    console.error('Error fetching allowed emails:', error)
    throw error
  }
}

/**
 * Add a new user to the allowedEmails collection
 * User will be able to sign in on next login
 * 
 * @param data - Email and role for the new user
 * @param currentUserEmail - Email of admin adding the user
 * @throws Error if not authorized or write fails
 */
export async function addUser(data: AddUserDTO, currentUserEmail: string): Promise<void> {
  try {
    const allowedEmailRef = doc(db, 'allowedEmails', data.email)
    
    // Check if email already exists
    const existingDoc = await getDoc(allowedEmailRef)
    if (existingDoc.exists()) {
      throw new Error('Email already exists in allowed list')
    }

    await setDoc(allowedEmailRef, {
      email: data.email,
      role: data.role,
      addedBy: currentUserEmail,
      addedAt: Timestamp.now(),
    })

    console.log('✅ User added to allowedEmails:', data.email)
  } catch (error) {
    console.error('Error adding user:', error)
    throw error
  }
}

/**
 * Remove a user from allowedEmails AND users collections
 * This will prevent them from signing in
 * 
 * Validations:
 * - Cannot remove yourself
 * - Cannot remove last admin (system must have at least 1 admin)
 * 
 * @param email - Email to remove from allowed list
 * @param currentUserEmail - Email of admin removing the user
 * @throws Error if trying to remove self, last admin, or not authorized
 */
export async function removeUser(email: string, currentUserEmail: string): Promise<void> {
  try {
    // 1. Prevent admin from removing themselves
    if (email === currentUserEmail) {
      throw new Error('No puedes eliminar tu propio acceso')
    }

    // 2. Check if user exists in users collection (primary source of truth)
    const usersRef = collection(db, 'users')
    const usersQuery = query(usersRef, where('email', '==', email))
    const usersSnapshot = await getDocs(usersQuery)
    
    if (usersSnapshot.empty) {
      throw new Error('Usuario no encontrado en el sistema')
    }

    const userDoc = usersSnapshot.docs[0]
    const userData = userDoc.data()
    const isAdmin = userData.role === 'admin'

    // 3. If user is admin, check if it's the last one
    if (isAdmin) {
      const adminsQuery = query(usersRef, where('role', '==', 'admin'))
      const adminsSnapshot = await getDocs(adminsQuery)
      
      if (adminsSnapshot.size <= 1) {
        throw new Error('No puedes eliminar el último administrador. Debe haber al menos un admin.')
      }
    }

    // 4. Delete from users collection
    await deleteDoc(doc(db, 'users', userDoc.id))
    console.log('🗑️ Deleted from users:', userDoc.id)

    // 5. Delete from allowedEmails (if exists - handles zombie users)
    const allowedEmailRef = doc(db, 'allowedEmails', email)
    const allowedEmailDoc = await getDoc(allowedEmailRef)
    
    if (allowedEmailDoc.exists()) {
      await deleteDoc(allowedEmailRef)
      console.log('🗑️ Deleted from allowedEmails:', email)
    } else {
      console.log('⚠️ User was not in allowedEmails (zombie user cleaned)')
    }

    console.log('✅ User completely removed:', email)
  } catch (error) {
    console.error('❌ Error removing user:', error)
    throw error
  }
}

/**
 * Update user role in both allowedEmails and users collections
 * 
 * Validations:
 * - Cannot change your own role
 * - Cannot downgrade last admin to user (system must have at least 1 admin)
 * 
 * @param data - User ID and new role
 * @param currentUserId - ID of admin making the change
 * @throws Error if trying to change own role, downgrade last admin, or not authorized
 */
export async function updateUserRole(data: UpdateUserRoleDTO, currentUserId: string): Promise<void> {
  try {
    // 1. Prevent admin from changing their own role
    if (data.userId === currentUserId) {
      throw new Error('No puedes cambiar tu propio rol')
    }

    // 2. Get user document to find email and current role
    const userRef = doc(db, 'users', data.userId)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado')
    }

    const userData = userDoc.data()
    const userEmail = userData.email
    const currentRole = userData.role

    // 3. If downgrading from admin to user, check if it's the last admin
    if (currentRole === 'admin' && data.newRole === 'user') {
      const allowedEmailsRef = collection(db, 'allowedEmails')
      const adminsQuery = query(
        allowedEmailsRef,
        where('role', '==', 'admin')
      )
      const adminsSnapshot = await getDocs(adminsQuery)
      
      if (adminsSnapshot.size <= 1) {
        throw new Error('No puedes degradar el último administrador. Debe haber al menos un admin.')
      }
    }

    // 4. Update role in users collection
    await updateDoc(userRef, {
      role: data.newRole,
    })

    // 5. Update role in allowedEmails collection
    const allowedEmailRef = doc(db, 'allowedEmails', userEmail)
    await updateDoc(allowedEmailRef, {
      role: data.newRole,
    })

    console.log('✅ User role updated:', userEmail, '→', data.newRole)
  } catch (error) {
    console.error('Error updating user role:', error)
    throw error
  }
}

/**
 * Get all plans from Firestore
 * @returns Array of plans
 */
export async function getPlans(): Promise<Plan[]> {
  try {
    const plansRef = collection(db, 'plans')
    const snapshot = await getDocs(plansRef)

    return snapshot.docs.map((docSnap) => ({
      id: Number(docSnap.id),
      ...docSnap.data(),
    })) as Plan[]
  } catch (error) {
    console.error('Error fetching plans:', error)
    throw error
  }
}

/**
 * Update images for a specific plan
 * 
 * @param planId - ID of the plan to update
 * @param images - New array of image URLs
 * @throws Error if not authorized or update fails
 */
export async function updatePlanImages(planId: number, images: string[]): Promise<void> {
  try {
    const planRef = doc(db, 'plans', planId.toString())
    
    await updateDoc(planRef, {
      images: images,
      updatedAt: new Date().toISOString(),
    })

    console.log('🖼️ Plan images updated:', planId)
  } catch (error) {
    console.error('Error updating plan images:', error)
    throw error
  }
}

/**
 * Get current user's role from Firestore
 * @param userId - Firebase Auth user ID
 * @returns User role or null if not found
 */
export async function getUserRole(userId: string): Promise<'admin' | 'user' | null> {
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      return null
    }

    return userDoc.data().role as 'admin' | 'user'
  } catch (error) {
    console.error('Error getting user role:', error)
    return null
  }
}
